import { EditableQuestion } from './types';

export type FormDataEntries = Record<string, string | Blob | string[]>;

const isScalar = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

/**
 * Flattens a nested payload into the bracket keys Rails reads from a
 * multipart body: `data_fields[0][options][1][value]`. Nothing but a string
 * or a file can travel in a form part, so a boolean becomes its word and a
 * null becomes an empty string, which the server reads as "clear it".
 *
 * A list of scalars travels as repeated `key[]` parts: Rack reads indexed
 * keys as a hash, which the controller's permit list refuses for an array.
 * The request core appends each item of an array entry under its key.
 */
export const toFormDataEntries = (
  payload: Record<string, unknown>,
): FormDataEntries => {
  const entries: FormDataEntries = {};
  const visit = (key: string, value: unknown) => {
    if (value === undefined) return;
    if (value === null) {
      entries[key] = '';
    } else if (value instanceof Blob) {
      entries[key] = value;
    } else if (Array.isArray(value) && value.every(isScalar)) {
      entries[`${key}[]`] = value.map(String);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => visit(`${key}[${index}]`, item));
    } else if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(
        ([name, item]) => visit(`${key}[${name}]`, item),
      );
    } else {
      entries[key] = String(value);
    }
  };
  Object.entries(payload).forEach(([key, value]) => visit(key, value));
  return entries;
};

/** A save with a picked or dropped image has to travel as multipart. */
export const needsMultipart = (questions: EditableQuestion[]): boolean =>
  questions.some(
    question =>
      !question._destroy &&
      (question.image instanceof Blob || Boolean(question.remove_image)),
  );
